import { useEffect, useState, type FormEvent } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import axios from 'axios'
import { Logo } from '../../components/brand/Logo'
import { Icon } from '../../components/ui/icons'
import { DocumentIdentityFooter } from '../../components/print/DocumentIdentityFooter'
import { formatDateTime } from '../../utils/format'

/**
 * Verificación pública de documentos clínicos por código (H5): el QR impreso
 * en una receta, solicitud de exámenes u orden de kinesiología apunta aquí.
 * Muestra lo justo para comprobar que el papel es auténtico y no fue alterado;
 * el RUT del paciente llega enmascarado desde el backend (Ley 21.719).
 */

interface VerifiedDocument {
  valid: boolean
  code?: string
  type?: 'PRESCRIPTION' | 'EXAM_REQUEST' | 'KINESIOLOGY'
  issuedAt?: string
  professional?: { name: string; rut?: string | null; registryNumber?: string | null; specialty?: string | null }
  patientName?: string
  patientRut?: string
  kinesicDiagnosis?: string | null
  sessions?: number | null
  items?: Array<{ name: string; dose?: string | null; route?: string | null; frequency?: string | null; duration?: string | null; quantity?: string | null }>
}

const TYPE_LABELS: Record<string, string> = {
  PRESCRIPTION: 'Receta médica',
  EXAM_REQUEST: 'Solicitud de exámenes',
  KINESIOLOGY: 'Orden de kinesiología',
}

export function VerifyDocumentPage() {
  const [searchParams] = useSearchParams()
  const [code, setCode] = useState(searchParams.get('codigo') ?? '')
  const [state, setState] = useState<'idle' | 'loading' | 'done' | 'error'>('idle')
  const [info, setInfo] = useState<VerifiedDocument | null>(null)

  async function verifyCode(value: string) {
    setState('loading')
    try {
      const { data } = await axios.get<VerifiedDocument>(`/api/public/clinical-orders/${encodeURIComponent(value.trim())}`)
      setInfo(data)
      setState('done')
    } catch {
      setState('error')
    }
  }

  useEffect(() => {
    const fromUrl = searchParams.get('codigo')
    if (fromUrl) void verifyCode(fromUrl)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function verify(e: FormEvent) {
    e.preventDefault()
    await verifyCode(code)
  }

  return (
    <div className="flex min-h-screen flex-col bg-surface">
      <header className="border-b border-steel-100 bg-white print:hidden">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-3">
          <Link to="/" aria-label="Cima Sport"><Logo className="h-9" /></Link>
          <Link to="/" className="text-sm font-medium text-brand-700 hover:underline">← Volver al sitio</Link>
        </div>
      </header>

      <main className="mx-auto w-full max-w-3xl flex-1 px-4 py-12">
        <h1 className="font-display text-2xl font-bold text-ink print:hidden">Verificación de documentos clínicos</h1>
        <p className="mt-1 text-steel-600 print:hidden">
          Ingresa el código impreso en la receta u orden (o escanea su código QR) para validar su autenticidad.
        </p>

        <form onSubmit={verify} className="mt-6 flex gap-2 print:hidden">
          <input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="p. ej. RX-1A2B3C4D5E"
            required
            className="w-full rounded-lg border border-steel-200 bg-white px-4 py-3 font-mono text-sm uppercase focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
          <button
            type="submit"
            disabled={state === 'loading'}
            className="rounded-full bg-brand-600 px-6 py-3 font-display text-sm font-semibold text-white transition-colors hover:bg-brand-700 disabled:opacity-60"
          >
            {state === 'loading' ? 'Verificando…' : 'Verificar'}
          </button>
        </form>

        {state === 'error' && (
          <p className="mt-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700 print:hidden">
            No pudimos verificar, intenta de nuevo.
          </p>
        )}

        {state === 'done' && info && !info.valid && (
          <div className="mt-6 rounded-2xl border-2 border-red-300 bg-red-50 p-6 text-center print:hidden">
            <p className="flex items-center justify-center gap-2 font-display text-xl font-bold text-red-700">
              <Icon name="close" className="h-5 w-5" />
              Código no válido
            </p>
            <p className="mt-1 text-sm text-red-600">
              El código no corresponde a un documento emitido por Cima Sport.
            </p>
          </div>
        )}

        {state === 'done' && info?.valid && (
          <div className="print-document mt-6 rounded-2xl border-2 border-emerald-400 bg-white p-6">
            <p className="flex items-center justify-center gap-2 text-center font-display text-xl font-bold text-emerald-800">
              <Icon name="check" className="h-5 w-5" />
              Documento auténtico
            </p>
            <p className="mt-1 text-center font-display text-lg text-ink">{TYPE_LABELS[info.type ?? ''] ?? 'Documento clínico'}</p>

            <dl className="mx-auto mt-5 max-w-md space-y-1.5 text-sm text-ink">
              <div className="flex justify-between gap-4"><dt className="font-medium">Código</dt><dd className="font-mono">{info.code}</dd></div>
              <div className="flex justify-between gap-4"><dt className="font-medium">Emitido</dt><dd>{info.issuedAt ? formatDateTime(info.issuedAt) : '—'}</dd></div>
              <div className="flex justify-between gap-4"><dt className="font-medium">Profesional</dt><dd>{info.professional?.name}</dd></div>
              {info.professional?.rut && (
                <div className="flex justify-between gap-4"><dt className="font-medium">RUT profesional</dt><dd className="font-mono">{info.professional.rut}</dd></div>
              )}
              {info.professional?.registryNumber && (
                <div className="flex justify-between gap-4"><dt className="font-medium">Registro</dt><dd>{info.professional.registryNumber}</dd></div>
              )}
              <div className="flex justify-between gap-4"><dt className="font-medium">Paciente</dt><dd>{info.patientName}</dd></div>
              <div className="flex justify-between gap-4"><dt className="font-medium">RUT paciente</dt><dd className="font-mono">{info.patientRut}</dd></div>
            </dl>

            {info.type === 'KINESIOLOGY' ? (
              <div className="mx-auto mt-4 max-w-md text-sm text-ink">
                <p><span className="font-medium">Diagnóstico kinésico:</span> {info.kinesicDiagnosis}</p>
                <p><span className="font-medium">Sesiones:</span> {info.sessions}</p>
              </div>
            ) : (
              (info.items ?? []).length > 0 && (
                <ol className="mx-auto mt-4 max-w-md space-y-1 text-sm text-ink">
                  {(info.items ?? []).map((i, idx) => (
                    <li key={idx}>
                      <span className="font-medium">{idx + 1}. {i.name}</span>
                      {i.quantity ? ` — ${i.quantity}` : ''}
                      {[i.dose, i.route, i.frequency, i.duration].filter(Boolean).length > 0 && (
                        <span className="block text-steel-600">
                          {[i.dose, i.route, i.frequency, i.duration].filter(Boolean).join(' · ')}
                        </span>
                      )}
                    </li>
                  ))}
                </ol>
              )
            )}

            <DocumentIdentityFooter verificationCode={info.code} kind="clinical-order" />

            <div className="mt-4 text-center print:hidden">
              <button
                onClick={() => window.print()}
                className="inline-flex items-center gap-2 rounded-full border border-steel-300 bg-white px-4 py-2 text-sm font-medium text-ink hover:bg-soft"
              >
                <Icon name="print" className="h-4 w-4" />
                Imprimir / guardar PDF
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  )
}
